import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IJsonApiResponse } from '../../interfaces/api/IJsonApiResponse';
import { environment } from '../../../environments/environment';
import { StaffAuthService } from '../staff-auth.service';
import { IStaffAccountDto } from '../../interfaces/dtos/staff/IStaffAccountDto';
import { IListQueryResponse } from '../../interfaces/api/IListQueryResponse';
import { IListRequestParams } from '../../interfaces/api/IListRequestParams';
import { HttpUtility } from '../../static-classes/HttpUtility';
import { IUpdateStaffAccountBody } from './interfaces/IUpdateStaffAccountBody';
import { IUpdateStaffAccountNameBody } from './interfaces/IUpdateStaffAccountNameBody';
import { IStaffAccountActiveSessionDto } from '../../interfaces/dtos/staff/IStaffAccountActiveSessionDto';

@Injectable({
  providedIn: 'root'
})
export class StaffAccountsService {
  private readonly HttpClient = inject(HttpClient);
  private readonly AuthService = inject(StaffAuthService);
  private readonly ApiBasePath: string = "/api/v1/staff/accounts";

  public ListStaffAccounts(params: IListRequestParams): Observable<IJsonApiResponse<IListQueryResponse<IStaffAccountDto>>> {
    const httpParams = HttpUtility.ObjectToHttpParams(params);
    return this.HttpClient.get<IJsonApiResponse<IListQueryResponse<IStaffAccountDto>>>(environment.API_HOST + this.ApiBasePath, {
      params: httpParams,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  public GetCurrentAccount(): Observable<IJsonApiResponse<IStaffAccountDto>> {
    return this.HttpClient.get<IJsonApiResponse<IStaffAccountDto>>(environment.API_HOST + this.ApiBasePath + "/current-account", {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Fetches a staff account by its Id.
   */
  public GetAccount(staffAccountId: number): Observable<IJsonApiResponse<IStaffAccountDto>> {
    return this.HttpClient.get<IJsonApiResponse<IStaffAccountDto>>(environment.API_HOST + this.ApiBasePath + `/${staffAccountId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Updates properties of a staff account.
   */
  public UpdateAccount(
    staffAccountId: number,
    body: IUpdateStaffAccountBody
  ): Observable<IJsonApiResponse<IStaffAccountDto>> {
    return this.HttpClient.patch<IJsonApiResponse<IStaffAccountDto>>(environment.API_HOST + this.ApiBasePath + `/${staffAccountId}`, body, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Allows the currently authenticated staff account to update its own display name.
   */
  public UpdateCurrentAccountName(
    body: IUpdateStaffAccountNameBody
  ): Observable<IJsonApiResponse<IStaffAccountDto>> {
    return this.HttpClient.patch<IJsonApiResponse<IStaffAccountDto>>(environment.API_HOST + this.ApiBasePath + "/current-account", body, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Fetches the current staff accounts active login sessions.
   */
  public GetCurrentAccountActiveSessions(params: IListRequestParams): Observable<IJsonApiResponse<IListQueryResponse<IStaffAccountActiveSessionDto>>> {
    const httpParams = HttpUtility.ObjectToHttpParams(params);
    return this.HttpClient.get<IJsonApiResponse<IListQueryResponse<IStaffAccountActiveSessionDto>>>(environment.API_HOST + this.ApiBasePath + "/current-account/active-sessions", {
      params: httpParams,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Revokes one of the current staff accounts own active login sessions.
   */
  public RevokeCurrentAccountActiveSession(sessionId: number): Observable<IJsonApiResponse<void>> {
    return this.HttpClient.delete<IJsonApiResponse<void>>(environment.API_HOST + this.ApiBasePath + `/current-account/active-sessions/${sessionId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Revokes all of the current staff accounts own active login sessions,
   * except the one used to make this request.
   */
  public RevokeAllCurrentAccountActiveSessions(): Observable<IJsonApiResponse<void>> {
    return this.HttpClient.delete<IJsonApiResponse<void>>(environment.API_HOST + this.ApiBasePath + "/current-account/active-sessions", {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Fetches a staff accounts active login sessions.
   */
  public GetAccountActiveSessions(staffAccountId: number, params: IListRequestParams): Observable<IJsonApiResponse<IListQueryResponse<IStaffAccountActiveSessionDto>>> {
    const httpParams = HttpUtility.ObjectToHttpParams(params);
    return this.HttpClient.get<IJsonApiResponse<IListQueryResponse<IStaffAccountActiveSessionDto>>>(environment.API_HOST + this.ApiBasePath + `/${staffAccountId}/active-sessions`, {
      params: httpParams,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Revokes one of a staff accounts active login sessions.
   */
  public RevokeAccountActiveSession(staffAccountId: number, sessionId: number): Observable<IJsonApiResponse<void>> {
    return this.HttpClient.delete<IJsonApiResponse<void>>(environment.API_HOST + this.ApiBasePath + `/${staffAccountId}/active-sessions/${sessionId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  /**
   * Revokes all of a staff accounts active login sessions.
   */
  public RevokeAllAccountActiveSessions(staffAccountId: number): Observable<IJsonApiResponse<void>> {
    return this.HttpClient.delete<IJsonApiResponse<void>>(environment.API_HOST + this.ApiBasePath + `/${staffAccountId}/active-sessions`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }

  public Logout(): Observable<IJsonApiResponse<void>> {
    return this.HttpClient.get<IJsonApiResponse<void>>(environment.API_HOST + this.ApiBasePath + "/logout", {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.AuthService.GetAuthToken() ?? ""}`
      }
    });
  }
}
